import { parseContract } from '@aibi/contracts';
import { AppError, err, ok, type Result } from '@aibi/shared';
import { z } from 'zod';
import { CSRF_HEADERS, sendJson, toServerError, type FetchFn } from '../../api/http-client';
import type { ApiClientOptions } from './auth-api';

const ADMIN = '/api/admin';

const ConnectionTypeSchema = z.object({
  id: z.string(),
  label: z.string(),
  kind: z.enum(['file', 'database']),
  extensions: z.array(z.string()),
  explore: z.boolean(),
  query: z.boolean(),
  note: z.string().nullable(),
});
export type ConnectionType = z.infer<typeof ConnectionTypeSchema>;

const ConnectorCatalogSchema = z.object({
  types: z.array(ConnectionTypeSchema).max(20),
  importEnabled: z.boolean(),
});
export type ConnectorCatalog = z.infer<typeof ConnectorCatalogSchema>;

const StoredFileSchema = z.object({
  fileId: z.string(),
  fileName: z.string(),
  connectorId: z.string(),
  sizeBytes: z.number(),
});
export type StoredFile = z.infer<typeof StoredFileSchema>;

const ImportSourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  folder: z.string(),
  connectorId: z.string(),
  sizeBytes: z.number(),
});
export type ImportSource = z.infer<typeof ImportSourceSchema>;

const ConnectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  connectorId: z.string(),
  status: z.enum(['pending', 'connected', 'error', 'unavailable']),
  lastTestedAt: z.string().nullable(),
  message: z.string().nullable(),
  warnings: z.array(z.string()),
  queryable: z.boolean(),
  fileName: z.string().optional(),
  host: z.string().optional(),
  port: z.number().optional(),
  database: z.string().optional(),
  schema: z.string().optional(),
  user: z.string().optional(),
  sslMode: z.string().optional(),
  httpPath: z.string().optional(),
  catalog: z.string().optional(),
  authType: z.enum(['pat', 'oauth-m2m', 'sql', 'entra-sp']).optional(),
  clientId: z.string().optional(),
  tenantId: z.string().optional(),
  encrypt: z.boolean().optional(),
  trustServerCertificate: z.boolean().optional(),
  /** Hay contrasena, token o secreto guardado; el servidor nunca devuelve su valor. */
  hasPassword: z.boolean(),
  models: z.array(z.object({ id: z.string(), name: z.string() })),
  discoveredAt: z.string().nullable(),
});
export type Connection = z.infer<typeof ConnectionSchema>;
export type ConnectionStatus = Connection['status'];

const ConnectionTestSchema = z.object({
  ok: z.literal(true),
  latencyMs: z.number(),
  warnings: z.array(z.string()),
  objects: z.number(),
});
export type ConnectionTest = z.infer<typeof ConnectionTestSchema>;

const Scalar = z.union([z.string(), z.number(), z.boolean()]).nullable();

export const COLUMN_ROLES = ['dimension', 'measure', 'both', 'none'] as const;
export type ColumnRole = (typeof COLUMN_ROLES)[number];
export const MAPPING_AGGREGATIONS = [
  'sum',
  'avg',
  'min',
  'max',
  'count',
  'count-distinct',
] as const;
export type MappingAggregation = (typeof MAPPING_AGGREGATIONS)[number];

const TableSchema = z.object({
  id: z.string(),
  name: z.string(),
  namespace: z.string().nullable(),
  kind: z.string(),
  status: z.string(),
  rows: z.number().nullable(),
  rowsApproximate: z.boolean(),
  issues: z.array(z.string()),
  columns: z.array(
    z.object({
      name: z.string(),
      logicalType: z.string(),
      nullable: z.boolean(),
      nullCount: z.number().nullable(),
      distinctCount: z.number().nullable(),
      min: Scalar,
      max: Scalar,
      redacted: z.boolean(),
      suggestedRole: z.enum(COLUMN_ROLES).catch('dimension'),
      suggestedAggregation: z.enum(MAPPING_AGGREGATIONS).nullable().catch(null),
      aggregations: z.array(z.enum(MAPPING_AGGREGATIONS)).catch([]),
    }),
  ),
});
export type DiscoveredTable = z.infer<typeof TableSchema>;
export type DiscoveredColumn = DiscoveredTable['columns'][number];

const RecommendationSchema = z.object({
  id: z.string(),
  kind: z.string(),
  group: z.enum(['dimension', 'measure', 'date', 'relationship', 'key']),
  label: z.string(),
  detail: z.string(),
  reason: z.string(),
  confidence: z.number(),
  objectIds: z.array(z.string()),
  suggested: z.boolean(),
});
export type Recommendation = z.infer<typeof RecommendationSchema>;
export type RecommendationGroup = Recommendation['group'];

const DiscoverySchema = z.object({
  connectionId: z.string(),
  discoveredAt: z.string(),
  status: z.string(),
  truncated: z.boolean(),
  issues: z.array(z.string()),
  tables: z.array(TableSchema),
  recommendations: z.array(RecommendationSchema),
});
export type Discovery = z.infer<typeof DiscoverySchema>;

const PreviewSchema = z.object({
  columns: z.array(z.object({ name: z.string(), logicalType: z.string() })),
  rows: z.array(z.array(z.unknown())),
  truncated: z.boolean(),
});
export type Preview = z.infer<typeof PreviewSchema>;

const NamedSchema = z.object({ id: z.string(), name: z.string() });
const DraftSchema = z.object({
  draftId: z.string(),
  modelId: z.string(),
  name: z.string(),
  tables: z.array(z.object({ name: z.string(), columns: z.number() })),
  dimensions: z.array(NamedSchema),
  measures: z.array(NamedSchema),
  relationships: z.number(),
  diagnostics: z.array(z.object({ severity: z.string(), message: z.string() })),
  validation: z.object({ ok: z.literal(true), rows: z.number() }),
});
export type Draft = z.infer<typeof DraftSchema>;

const PublicationSchema = z.object({
  modelId: z.string(),
  name: z.string(),
  connectionId: z.string(),
  publishedAt: z.string(),
});
export type Publication = z.infer<typeof PublicationSchema>;

export interface FileConnectionInput {
  readonly connectorId: 'csv' | 'parquet' | 'excel';
  readonly name: string;
  readonly fileId?: string;
}

export interface PgConnectionInput {
  readonly connectorId: 'postgresql';
  readonly name: string;
  readonly host: string;
  readonly port: number;
  readonly database: string;
  readonly schema?: string;
  readonly user: string;
  /** Vacia al editar: el servidor conserva la guardada. */
  readonly password?: string;
  readonly sslMode: 'disable' | 'prefer' | 'require' | 'verify-ca' | 'verify-full';
}

export type DatabricksAuthType = 'pat' | 'oauth-m2m';

export interface DatabricksConnectionInput {
  readonly connectorId: 'databricks';
  readonly name: string;
  readonly host: string;
  readonly httpPath: string;
  readonly catalog?: string;
  readonly schema?: string;
  readonly authType: DatabricksAuthType;
  readonly clientId?: string;
  /** Token personal o secreto del service principal; vacio al editar: se conserva. */
  readonly secret?: string;
}

export type SqlServerAuthType = 'sql' | 'entra-sp';

export interface SqlServerConnectionInput {
  readonly connectorId: 'sqlserver';
  readonly name: string;
  readonly host: string;
  readonly port: number;
  readonly database: string;
  readonly schema?: string;
  readonly authType: SqlServerAuthType;
  readonly user?: string;
  readonly tenantId?: string;
  readonly clientId?: string;
  readonly encrypt: boolean;
  readonly trustServerCertificate: boolean;
  /** Contrasena del login SQL o secreto del service principal; vacio al editar: se conserva. */
  readonly secret?: string;
}

export type MysqlSslMode = 'verify-full' | 'require' | 'disable';

export interface MysqlConnectionInput {
  readonly connectorId: 'mysql';
  readonly name: string;
  readonly host: string;
  readonly port: number;
  readonly database: string;
  readonly user: string;
  readonly password?: string;
  readonly sslMode: MysqlSslMode;
}

export type RemoteConnectionInput =
  PgConnectionInput | DatabricksConnectionInput | SqlServerConnectionInput | MysqlConnectionInput;

export type ConnectionInput = FileConnectionInput | RemoteConnectionInput;

export const isRemoteInput = (input: ConnectionInput): input is RemoteConnectionInput =>
  input.connectorId !== 'csv' && input.connectorId !== 'parquet' && input.connectorId !== 'excel';
/** Prueba de un borrador; `connectionId` reutiliza el secreto guardado al editar. */
export type ConnectionTestInput =
  FileConnectionInput | (RemoteConnectionInput & { readonly connectionId?: string });

export interface ColumnAssignment {
  readonly objectId: string;
  readonly column: string;
  readonly role: ColumnRole;
  readonly aggregation?: MappingAggregation;
}

export interface DraftInput {
  readonly connectionId: string;
  readonly objectIds: readonly string[];
  readonly name: string;
  /** Con `columns`, el servidor solo aplica las relaciones y claves aceptadas. */
  readonly acceptedRecommendationIds: readonly string[];
  readonly columns?: readonly ColumnAssignment[];
}

type R<T> = Promise<Result<T, AppError>>;

/**
 * Cliente de administracion (`/api/admin/*`). Requiere sesion de administrador: el
 * servidor valida cada peticion, ocultar controles en la UI no protege nada.
 */
export interface AdminApi {
  connectors(): R<ConnectorCatalog>;
  upload(file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal): R<StoredFile>;
  importSources(): R<readonly ImportSource[]>;
  importFile(sourceId: string): R<StoredFile>;
  connections(): R<readonly Connection[]>;
  testDraft(input: ConnectionTestInput): R<ConnectionTest>;
  create(input: ConnectionInput): R<Connection>;
  update(id: string, input: ConnectionInput): R<Connection>;
  remove(id: string): R<void>;
  testSaved(id: string): R<Connection>;
  discover(id: string): R<Discovery>;
  lastDiscovery(id: string): R<Discovery | null>;
  preview(id: string, objectId: string): R<Preview>;
  publications(): R<readonly Publication[]>;
  createDraft(input: DraftInput): R<Draft>;
  publish(draftId: string): R<Publication>;
  unpublish(modelId: string): R<void>;
}

/**
 * Sube un archivo en streaming (`application/octet-stream`) con progreso. `fetch` no
 * informa del progreso de subida, por eso se usa `XMLHttpRequest`.
 */
function uploadWithProgress(
  url: string,
  file: File,
  onProgress: ((fraction: number) => void) | undefined,
  signal: AbortSignal | undefined,
): R<StoredFile> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.setRequestHeader('Accept', 'application/json');
    for (const [name, value] of Object.entries(CSRF_HEADERS)) xhr.setRequestHeader(name, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress?.(event.loaded / event.total);
    };
    xhr.onerror = () => {
      resolve(err(new AppError('UNAVAILABLE', 'No se pudo contactar con el servidor.')));
    };
    xhr.onabort = () => {
      resolve(err(new AppError('CANCELLED', 'Subida cancelada.')));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        let payload: unknown;
        try {
          payload = JSON.parse(xhr.responseText) as unknown;
        } catch {
          resolve(err(new AppError('VALIDATION_ERROR', 'La respuesta no es JSON valido.')));
          return;
        }
        resolve(parseContract(StoredFileSchema, payload, 'contrato de respuesta'));
        return;
      }
      void toServerError(new Response(xhr.responseText, { status: xhr.status })).then((error) => {
        resolve(err(error));
      });
    };
    signal?.addEventListener('abort', () => {
      xhr.abort();
    });
    xhr.send(file);
  });
}

/**
 * @example
 * const admin = createAdminApi({ baseUrl: '', fetch: window.fetch.bind(window) });
 * const catalog = await admin.connectors();
 */
export function createAdminApi({ baseUrl, fetch }: ApiClientOptions): AdminApi {
  const url = (path: string) => `${baseUrl}${ADMIN}${path}`;
  const id = (value: string) => encodeURIComponent(value);
  const call = <S extends z.ZodType>(
    fetchFn: FetchFn,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    schema: S,
    body?: unknown,
  ) => sendJson(fetchFn, method, url(path), schema, body === undefined ? {} : { body });
  const unwrapField = async <T, K extends string>(
    result: Promise<Result<Record<K, T>, AppError>>,
    key: K,
  ): R<T> => {
    const value = await result;
    return value.ok ? ok(value.value[key]) : value;
  };

  return {
    connectors: () => call(fetch, 'GET', '/connectors', ConnectorCatalogSchema),
    upload: (file, onProgress, signal) =>
      uploadWithProgress(
        url(`/files?name=${encodeURIComponent(file.name)}`),
        file,
        onProgress,
        signal,
      ),
    importSources: () =>
      unwrapField(
        call(fetch, 'GET', '/import-sources', z.object({ sources: z.array(ImportSourceSchema) })),
        'sources',
      ),
    importFile: (sourceId) => call(fetch, 'POST', '/files/import', StoredFileSchema, { sourceId }),
    connections: () =>
      unwrapField(
        call(fetch, 'GET', '/connections', z.object({ connections: z.array(ConnectionSchema) })),
        'connections',
      ),
    testDraft: (input) => call(fetch, 'POST', '/connections/test', ConnectionTestSchema, input),
    create: (input) => call(fetch, 'POST', '/connections', ConnectionSchema, input),
    update: (connectionId, input) =>
      call(fetch, 'PUT', `/connections/${id(connectionId)}`, ConnectionSchema, input),
    remove: (connectionId) =>
      call(fetch, 'DELETE', `/connections/${id(connectionId)}`, z.undefined()),
    testSaved: (connectionId) =>
      call(fetch, 'POST', `/connections/${id(connectionId)}/test`, ConnectionSchema),
    discover: (connectionId) =>
      call(fetch, 'POST', `/connections/${id(connectionId)}/discover`, DiscoverySchema),
    lastDiscovery: (connectionId) =>
      unwrapField(
        call(
          fetch,
          'GET',
          `/connections/${id(connectionId)}/discovery`,
          z.object({ discovery: DiscoverySchema.nullable() }),
        ),
        'discovery',
      ),
    preview: (connectionId, objectId) =>
      call(
        fetch,
        'GET',
        `/connections/${id(connectionId)}/preview?object=${encodeURIComponent(objectId)}`,
        PreviewSchema,
      ),
    publications: () =>
      unwrapField(
        call(fetch, 'GET', '/publications', z.object({ publications: z.array(PublicationSchema) })),
        'publications',
      ),
    createDraft: (input) => call(fetch, 'POST', '/publications/draft', DraftSchema, input),
    publish: (draftId) => call(fetch, 'POST', '/publications', PublicationSchema, { draftId }),
    unpublish: (modelId) => call(fetch, 'DELETE', `/models/${id(modelId)}`, z.undefined()),
  };
}
